/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Collapse,
  Popover,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { displayOptimizerWarning } from "../utils/promoWarningLabels";

// ─── Warning severity helpers ─────────────────────────────────────────────────

function getWarningSeverityConfig(severity) {
  switch (String(severity || "").toLowerCase()) {
    case "critical":
      return { color: "#B91C1C", bg: "#FEF2F2", border: "#FCA5A5", label: "Critical" };
    case "high":
      return { color: "#C2410C", bg: "#FFF7ED", border: "#FDBA74", label: "High" };
    case "medium":
      return { color: "#B45309", bg: "#FFFBEB", border: "#FDE68A", label: "Medium" };
    case "info":
    default:
      return { color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", label: "Info" };
  }
}

function severityOrder(severity) {
  const map = { critical: 0, high: 1, medium: 2, info: 3 };
  return map[String(severity || "").toLowerCase()] ?? 3;
}

// ─── Warning grouping ─────────────────────────────────────────────────────────

const TARGET_WARNING_CODES = new Set([
  "CM_TARGET_NOT_ACHIEVED", "VOLUME_TARGET_NOT_ACHIEVED",
  "TARGET_CM_NOT_ACHIEVED", "TARGET_CM_COULD_NOT_BE_ACHIEVED",
  "TARGET_NOT_ACHIEVED", "TARGET_VOLUME_NOT_ACHIEVED",
]);

const BUSINESS_RISK_CODES = new Set([
  "LOW_ROI", "CANNIBALIZATION_RISK", "HIGH_CANNIBALIZATION",
  "MUST_PROMOTE", "MUST_PROMOTE_WEEKS", "NEGATIVE_MARGIN", "LOW_MARGIN",
  "POOR_ROI", "BELOW_THRESHOLD", "MARGIN_AT_RISK",
  "MUST_PROMOTE_RULE_APPLIED", "CM_BELOW_HISTORICAL_AVERAGE",
  "DISCOUNT_OUTSIDE_ALLOWED_BAND",
]);

const DATA_QUALITY_CODES = new Set([
  "MISSING_DATA", "NO_HISTORICAL_DATA", "DATA_INSUFFICIENT",
  "INSUFFICIENT_DATA", "LOW_CONFIDENCE", "DATA_GAP",
  "COST_FALLBACK_USED", "CANNIBALIZATION_VALUE_MISSING",
]);

function groupWarnings(warnings) {
  const target = [], businessRisk = [], dataQuality = [];
  for (const w of warnings) {
    const code = String(w.code || "").toUpperCase();
    if (TARGET_WARNING_CODES.has(code) || w.scope === "plan") {
      target.push(w);
    } else if (DATA_QUALITY_CODES.has(code)) {
      dataQuality.push(w);
    } else if (BUSINESS_RISK_CODES.has(code) || w.scope === "sku_week") {
      businessRisk.push(w);
    } else {
      businessRisk.push(w);
    }
  }
  return { target, businessRisk, dataQuality };
}

// ─── Normalize warnings from API response ────────────────────────────────────

function normalizeWarningItem(item) {
  if (item == null) return null;
  if (typeof item === "string") {
    const s = item.trim();
    if (!s) return null;
    return { code: s, severity: "medium", message: displayOptimizerWarning(s), scope: "plan" };
  }
  if (typeof item === "object") {
    const msg = item.message != null ? String(item.message).trim() : null;
    if (!msg && !item.code) return null;
    return {
      code: item.code ?? null,
      severity: item.severity ?? "medium",
      message: msg || displayOptimizerWarning(item.code) || item.code,
      scope: item.scope ?? null,
      metric_value: item.metric_value ?? null,
      threshold: item.threshold ?? null,
      sku_name: item.sku_name ?? null,
      week: item.week ?? null,
    };
  }
  return null;
}

export function normalizeWarnings(payload) {
  const w = Array.isArray(payload) ? payload : payload?.warnings;
  if (w == null) return [];
  if (Array.isArray(w)) {
    return w.map(normalizeWarningItem).filter(Boolean).sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
  }
  if (typeof w === "string" && w.trim()) {
    const item = normalizeWarningItem(w);
    return item ? [item] : [];
  }
  return [];
}

// ─── WarningBadge: clickable badge that opens a detail popover ───────────────

export function WarningBadge({ warning, skuName, week, compact = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const cfg = getWarningSeverityConfig(warning.severity);
  const open = Boolean(anchorEl);

  const displayLabel = displayOptimizerWarning(warning.code || warning.message) || warning.message || warning.code || "Warning";
  const resolvedSku = skuName || warning.sku_name || null;
  const resolvedWeek = week ?? warning.week ?? null;
  const SeverityIcon = warning.severity === "critical" || warning.severity === "high" || warning.severity === "medium"
    ? WarningAmberIcon
    : InfoOutlinedIcon;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Warning: ${displayLabel} — click for details`}
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setAnchorEl(e.currentTarget); } }}
        sx={{
          display: "inline-flex",
          alignItems: "flex-start",
          gap: 0.55,
          px: compact ? 0.7 : 1,
          py: compact ? 0.3 : 0.5,
          borderRadius: 1,
          backgroundColor: cfg.bg,
          border: `1px solid ${cfg.border}`,
          cursor: "pointer",
          maxWidth: "100%",
          transition: "opacity 0.15s",
          "&:hover": { opacity: 0.8 },
          "&:focus-visible": { outline: `2px solid ${cfg.color}`, outlineOffset: 1 },
        }}
      >
        <SeverityIcon sx={{ fontSize: compact ? 13 : 15, color: cfg.color, flexShrink: 0, mt: "1px" }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: compact ? 11 : 13,
            fontWeight: 600,
            color: cfg.color,
            lineHeight: 1.35,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {displayLabel}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 6,
          sx: { borderRadius: 2, maxWidth: 340, width: "100%", border: `1.5px solid ${cfg.border}`, overflow: "hidden" },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, backgroundColor: cfg.bg, borderBottom: `1px solid ${cfg.border}` }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <SeverityIcon sx={{ fontSize: 20, color: cfg.color, mt: 0.2, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  label={cfg.label}
                  size="small"
                  sx={{
                    height: 22, fontSize: 12, fontWeight: 700,
                    backgroundColor: `${cfg.color}20`, color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              </Stack>
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 14, color: "text.primary", mt: 0.5, lineHeight: 1.4, wordBreak: "break-word" }}>
                {warning.message || displayLabel}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 1.25, backgroundColor: "#fff" }}>
          {resolvedSku && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 0.5, gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, flexShrink: 0 }}>SKU</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary", textAlign: "right", wordBreak: "break-word" }}>{resolvedSku}</Typography>
            </Stack>
          )}
          {resolvedWeek != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Week</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>Week {resolvedWeek}</Typography>
              </Stack>
            </>
          )}
          {warning.metric_value != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Metric value</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>{String(warning.metric_value)}</Typography>
              </Stack>
            </>
          )}
          {warning.threshold != null && (
            <>
              <Divider sx={{ borderColor: "#F1F5F9" }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Threshold</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, color: "text.primary" }}>{String(warning.threshold)}</Typography>
              </Stack>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}

// ─── Warning group sections ───────────────────────────────────────────────────

function WarningGroupSection({ title, warnings, icon: Icon, accentColor }) {
  const [collapsed, setCollapsed] = useState(false);
  if (warnings.length === 0) return null;
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.75, cursor: "pointer", userSelect: "none" }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Icon sx={{ fontSize: 14, color: accentColor }} />
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, color: accentColor, textTransform: "uppercase", letterSpacing: 0.05 }}>
            {title}
          </Typography>
          <Chip
            label={warnings.length}
            size="small"
            sx={{ height: 16, fontSize: 10, fontWeight: 700, backgroundColor: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30`, "& .MuiChip-label": { px: 0.6 } }}
          />
        </Stack>
        <ExpandMoreIcon sx={{ fontSize: 16, color: accentColor, transform: collapsed ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.2s" }} />
      </Stack>
      <Collapse in={!collapsed}>
        <Stack spacing={0.6} sx={{ pl: 0.5 }}>
          {warnings.map((w, i) => (
            <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ─── Main WarningsSection component ──────────────────────────────────────────

export default function WarningsSection({ warnings: rawWarnings, title = "Simulation warnings" }) {
  const warnings = normalizeWarnings(rawWarnings);
  const hasWarnings = warnings.length > 0;
  const { target, businessRisk, dataQuality } = groupWarnings(warnings);

  const highestSev = hasWarnings
    ? warnings.reduce((best, w) => (severityOrder(w.severity) < severityOrder(best) ? w.severity : best), "info")
    : "info";
  const accentCfg = getWarningSeverityConfig(highestSev);
  const leftAccent = hasWarnings ? accentCfg.color : "#CBD5E1";

  return (
    <Box sx={{ mb: 2.5 }}>
      <Paper elevation={0} sx={{
        borderRadius: 2.5, border: "1px solid #E8ECF0", borderLeft: `4px solid ${leftAccent}`,
        overflow: "hidden", backgroundColor: "#fff", transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 20px rgba(15,23,42,0.06)" },
      }}>
        <Box px={2.25} py={1.75} sx={{ borderBottom: "1px solid #E8ECF0", backgroundColor: "#fff" }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
              backgroundColor: hasWarnings ? `${accentCfg.color}12` : "#F4F6F9",
              color: hasWarnings ? accentCfg.color : "text.secondary",
            }}>
              {hasWarnings ? <WarningAmberIcon sx={{ fontSize: 22 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.15 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ fontSize: 13 }}>
                  {hasWarnings ? title : "No warnings"}
                </Typography>
                {hasWarnings && (
                  <Chip label={`${warnings.length} warning${warnings.length !== 1 ? "s" : ""}`} size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: `${accentCfg.color}15`, color: accentCfg.color, border: `1px solid ${accentCfg.color}30`, "& .MuiChip-label": { px: 0.85 } }}
                  />
                )}
              </Stack>
              {!hasWarnings && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: 13 }}>
                  This simulation did not return any warnings.
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        {hasWarnings && (
          <Box px={2.25} py={1.75} sx={{ backgroundColor: "#fff" }}>
            <Stack spacing={2}>
              <WarningGroupSection title="Target warnings" warnings={target} icon={HighlightOffIcon} accentColor={getWarningSeverityConfig("high").color} />
              <WarningGroupSection title="Business risk" warnings={businessRisk} icon={WarningAmberIcon} accentColor={getWarningSeverityConfig("medium").color} />
              <WarningGroupSection title="Data quality notes" warnings={dataQuality} icon={InfoOutlinedIcon} accentColor={getWarningSeverityConfig("info").color} />
              {target.length === 0 && businessRisk.length === 0 && dataQuality.length === 0 && (
                <Stack spacing={0.6}>
                  {warnings.map((w, i) => (
                    <WarningBadge key={`${w.code ?? w.message}_${i}`} warning={w} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
